import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Badge } from "@/ds/shadcn/components/ui/badge";
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator } from "@/ds/shadcn/components/ui/dropdown-menu";
import type { PriceAlertDetailsModel } from "@/features/priceAlerts/api/models/PriceAlertDetailsModel";
import { usePriceAlertUpdate } from "@/features/priceAlerts/api/usePriceAlertUpdate";
import { priceAlertMetricIsPercentage, type PriceAlertMetric } from "@/features/priceAlerts/PriceAlertMetric";
import type { Currency } from "@/lib/domain/Currency";
import { BellIcon, BellOffIcon, EyeIcon, Trash2Icon } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router";

export type PriceAlertsDataViewProps = PriceAlertDetailsModel & {
	onDeletePriceAlert: (priceAlertId: string) => void;
	onEditPriceAlert: (priceAlert: PriceAlertDetailsModel) => void;
	onViewPriceAlert: (targetLinkId: string) => void;
};

interface PriceAlertsMetricDisplayProps {
	metricType: PriceAlertMetric;
	metric: number;
	currency: Currency;
}

export const PriceAlertsMetricDisplay = (props: PriceAlertsMetricDisplayProps) => {
	const {
		metricType,
		metric,
		currency
	} = props;

	return priceAlertMetricIsPercentage(metricType)
		? (
			<>{metric}%</>
		) : (
			<MoneyDisplay
				money={{ value: metric, currency }}
				mode="Standard"
				preferredCurrency={currency} />
		);
};

interface PriceAlertsStatusBadgeProps {
	enabled: boolean;
}

export const PriceAlertsStatusBadge = (props: PriceAlertsStatusBadgeProps) => {
	const {
		enabled
	} = props;

	return (
		<Badge className="w-20" variant={enabled ? "default" : "secondary"}>
			{
				enabled ? "Active" : "Triggered"
			}
		</Badge>
	);
};

interface PriceAlertsMenuProps {
	priceAlert: PriceAlertDetailsModel;
	onEnabledPriceAlert: () => void;
	onViewPriceAlert: (targetLinkId: string) => void;
	onDeletePriceAlert: (priceAlertId: string) => void;
}

export const PriceAlertsMenu: FC<PriceAlertsMenuProps> = props => {
	const {
		priceAlert,
		onEnabledPriceAlert,
		onViewPriceAlert,
		onDeletePriceAlert
	} = props;

	return (
		<DropdownMenuGroup>
			{
				priceAlert.targetType === "Variant"
					? (
						<DropdownMenuItem onClick={() => onViewPriceAlert(priceAlert.targetLinkId)}>
							<EyeIcon />
							View Product
						</DropdownMenuItem>
					) : (
						<Link to={`/collections/${priceAlert.targetLinkId}`}>
							<DropdownMenuItem>
								<EyeIcon />
								View Collection
							</DropdownMenuItem>
						</Link>
					)
			}

			<DropdownMenuItem onClick={onEnabledPriceAlert}>
				{
					priceAlert.enabled
						? (
							<>
								<BellOffIcon />
								Disable
							</>
						) : (
							<>
								<BellIcon />
								Enable
							</>
						)
				}
			</DropdownMenuItem>
			<DropdownMenuSeparator />
			<DropdownMenuItem variant="destructive" onClick={() => onDeletePriceAlert(priceAlert.priceAlertId)}>
				<Trash2Icon />
				Delete
			</DropdownMenuItem>
		</DropdownMenuGroup>
	);
};

export const toggleEnabledPriceAlert = (priceAlert: PriceAlertDetailsModel, updatePriceAlert: ReturnType<typeof usePriceAlertUpdate>) => {
	updatePriceAlert.mutate({
		priceAlertId: priceAlert.priceAlertId,
		requestModel: {
			enabled: !priceAlert.enabled,
			targetType: priceAlert.targetType,
			targetId: priceAlert.targetId,
			metricType: priceAlert.metricType,
			metric: priceAlert.metric,
			currency: priceAlert.currency,
			step: priceAlert.step,
			priceSource: priceAlert.priceSource
		}
	});
};
